import {
    Box,
    Typography,
    LinearProgress,
    Chip,
    Paper,
    Grid
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TaskIcon from '@mui/icons-material/Task';
import DescriptionIcon from '@mui/icons-material/Description';
import CircularProgress from '@mui/material/CircularProgress';
import DoneIcon from '@mui/icons-material/Done';
const TicketProcessPanel = ({ progress = 0 }: { progress: number }) => {
    const step = progress < 33 ? 0 : progress < 66 ? 1 : 2;

    const steps = [
        {
            label: 'Preparando solicitud',
            icon: <DescriptionIcon fontSize="small" />,
            sub: 'Recopilando los datos del ticket',
            tasks: ['Validar campos', 'Verificar formato', 'Comprobar duplicados']
        },
        {
            label: 'Gestionando archivos adjuntos',
            icon: <UploadFileIcon fontSize="small" />,
            sub: 'Subiendo imágenes y documentos',
            tasks: ['Subir archivos',]
        },
        {
            label: 'Registrando caso',
            icon: <TaskIcon fontSize="small" />,
            sub: 'Completando la solicitud',
            tasks: ['Generar ID único', 'Asignar departamento', 'Enviar notificaciones']
        }
    ];

    const getStatusChip = (index: number) => {
        if (index < step)
            return <Chip label="Completado" size="small" sx={{ backgroundColor: '#34D399', color: '#fff', fontWeight: 600 }} />;
        if (index === step)
            return <Chip label="En proceso" size="small" sx={{ backgroundColor: '#DBEAFE', color: '#2563EB', fontWeight: 600 }} />;
        return <Chip label="Pendiente" size="small" sx={{ backgroundColor: '#E5E7EB', color: '#6B7280', fontWeight: 600 }} />;
    };

    return (
        <Box sx={{ p: 4, backgroundColor: '#fff', borderRadius: 3 }}>
            {/* Header global */}
            <Box display="flex" alignItems="center" gap={2} mb={2}>
                <AccessTimeIcon sx={{ fontSize: 32, color: '#3B82F6' }} />
                <Box>
                    <Typography variant="h6" fontWeight="bold">
                        Procesando tu ticket
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Estamos trabajando en tu solicitud
                    </Typography>
                </Box>
            </Box>

            {/* Progreso */}
            <Box mb={4}>
                <Typography fontWeight={600} fontSize={14} mb={1}>
                    Progreso General
                </Typography>
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                        height: 8,
                        backgroundColor: '#E5E7EB',
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: '#3B82F6'
                        },
                        borderRadius: 4
                    }}
                />
                <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography fontSize={12} color="text.secondary">Iniciado</Typography>
                    <Typography fontSize={12} color="text.secondary">Paso {step + 1} de 3</Typography>
                    <Typography fontSize={12} fontWeight={600} color="#2563EB">{progress}% completado</Typography>
                </Box>
            </Box>

            {/* Grid general */}
            <Grid container spacing={3}>
                {/* Columna izquierda con todos los pasos */}
                <Grid xs={12} md={8}>
                    <Typography fontWeight={600} fontSize={14} mb={2}>
                        Estado del Proceso
                    </Typography>

                    {steps.map((s, i) => {
                        const isCurrent = i === step;
                        const isCompleted = i < step;

                        return (
                            <Paper
                                key={i}
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    borderColor: isCurrent
                                        ? '#BFDBFE'
                                        : isCompleted
                                            ? 'transparent'
                                            : '#E5E7EB',
                                    backgroundColor: isCurrent
                                        ? '#EFF6FF'
                                        : isCompleted
                                            ? 'rgb(203, 227, 217)'
                                            : '#F9FAFB',
                                    borderRadius: 3
                                }}
                            >

                                <Box display="flex" alignItems="center" gap={2}>
                                    <Box
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor: isCurrent ? '#DBEAFE' : '#E5E7EB',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: isCurrent ? '#2563EB' : '#6B7280'
                                        }}
                                    >
                                        {isCompleted ? (
                                            <DoneIcon fontSize="small" sx={{ color: '#10B981' }} />
                                        ) : isCurrent ? (
                                            <CircularProgress size={16} thickness={5} sx={{ color: '#2563EB' }} />
                                        ) : (
                                            s.icon
                                        )}
                                    </Box>

                                    <Box>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography fontWeight={600} color={isCurrent ? '#2563EB' : 'text.secondary'}>
                                                {s.label}
                                            </Typography>
                                            {getStatusChip(i)}
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">
                                            {s.sub}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box mt={1} display="flex" flexWrap="wrap" gap={1} ml={6}>
                                    {s.tasks.map((t, idx) => (
                                        <Chip
                                            key={idx}
                                            label={`• ${t}`}
                                            size="small"
                                            sx={{
                                                backgroundColor: '#E5E7EB',
                                                color: '#4B5563',
                                                fontWeight: 500,
                                                fontSize: 12
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Paper>
                        );
                    })}
                </Grid>

                {/* Columna derecha: mensaje lateral + info extra */}
                <Grid xs={12} md={4}>
                    <Box
                        sx={{
                            height: '100%',
                            border: '1px solid #BFDBFE',
                            borderRadius: 3,
                            backgroundColor: '#EFF6FF',
                            p: 3
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <AccessTimeIcon fontSize="small" sx={{ color: '#2563EB' }} />
                            <Typography fontWeight={600} color="#2563EB">
                                Procesando solicitud
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" mb={2} sx={{ textAlign: 'justify' }}>
                            Mantén esta ventana abierta mientras completamos tu solicitud.
                        </Typography>

                        {/* Consejo */}
                        <Box
                            sx={{
                                border: '1px solid #FCD34D',
                                backgroundColor: '#FFFBEB',
                                borderRadius: 2,
                                p: 2,
                                mb: 2
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Typography fontWeight={600} color="#D97706">
                                    ⚠️ Seguridad
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="#92400E" sx={{ textAlign: 'justify' }}>
                                Estamos validando tus datos para asegurar que toda la información esté correcta.
                            </Typography>
                        </Box>

                        {/* Optimización */}
                        <Box
                            sx={{
                                border: '1px solid rgb(191, 227, 212)',
                                backgroundColor: '#ECFDF5',
                                borderRadius: 2,
                                p: 2
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Typography fontWeight={600} color="#059669">
                                    ⚡ Optimización
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="#065F46" sx={{ textAlign: 'justify' }}>
                                Estamos optimizando tu solicitud para un procesamiento más rápido.
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default TicketProcessPanel;
